import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import Banner from "@/components/ui/banner";
import FileUpload from "@/components/controls/FileUpload/FileUpload";
import { ActivityData } from "@/entities/activities/types";
import {
  useTranscriptUpload,
  isValidTranscriptFile,
  ACCEPTED_TRANSCRIPT_EXTENSIONS
} from "@/entities/activities/transcriptHooks";

interface ImportTranscriptModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: ActivityData;
  pageName: string;
  entityId: string;
  hasExistingTranscript?: boolean;
  onSuccess?: () => void;
}

const ImportTranscriptModal: React.FC<ImportTranscriptModalProps> = ({
  isOpen,
  onClose,
  activity,
  pageName,
  entityId,
  hasExistingTranscript = false,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { uploadTranscript, isUploading } = useTranscriptUpload({
    pageName,
    entityId,
    activityId: activity.id,
    activity,
    onSuccess: () => {
      setSelectedFile(null);
      setUploadError(null);
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      setUploadError(error.message);
    }
  });

  const handleRawFileSelect = (file: File) => {
    setUploadError(null);
    if (!isValidTranscriptFile(file)) {
      setUploadError(t("activities.transcript.invalidFileType"));
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (selectedFile) {
      await uploadTranscript(selectedFile);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadError(null);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => {
        if (!open) {
          handleClose();
        }
      }}
      contentClassname="w-[35rem]"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {t("activities.transcript.importTitle")}
        </Text>
      }
      content={
        <div className="flex flex-col gap-4 px-6 pt-4 pb-4">
          {hasExistingTranscript && (
            <Banner
              variant="warning"
              title={t("activities.transcript.replaceWarning")}
              titleVariant="body"
            />
          )}

          <FileUpload
            isSingleFile={true}
            accept={ACCEPTED_TRANSCRIPT_EXTENSIONS}
            onRawFileSelect={handleRawFileSelect}
            externalError={uploadError}
            dragAndDropText={t("activities.transcript.dragAndDrop")}
            description={t("activities.transcript.acceptedFormats")}
          />
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isUploading}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading
              ? t("activities.transcript.uploading")
              : t("activities.transcript.save")}
          </Button>
        </div>
      }
    />
  );
};

export default ImportTranscriptModal;
